import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { FilesService } from '../../common/_services/files.service';
import { File as GuaiFile } from '../../common/_types/file';
import { MailboxService } from '../_services/mailbox.service';
import {
  MailboxComposeMode,
  MailboxDraft,
  MailboxError,
  MailboxForwardedPart,
  MailboxSendRequest
} from '../_types/mailbox';

/// Compositor del buzón: redactar, responder, responder a todos y reenviar.
///
/// Es propio y no reutiliza `ComunicationsComponent`, que es el que envía desde la ficha de expediente. Aquel está
/// orientado a registrar una comunicación de un expediente y usa otro endpoint, que no deja copia en la carpeta de
/// enviados ni enlaza la respuesta con la conversación; y son 718 líneas con tres formularios y diez pantallas que
/// lo abren, sin una sola prueba en el repositorio. Lo que sí se comparte es el campo de destinatarios
/// (`app-address-chips`) y el patrón de subida de ficheros en dos pasos.
///
/// El borrador (destinatarios, asunto y cita del original) lo prepara el servidor: es quien tiene el mensaje y quien
/// sabe sanearlo antes de citarlo.
@Component({
    selector: 'app-compose-dialog',
    templateUrl: './compose-dialog.component.html',
    styleUrls: ['./compose-dialog.component.css'],
    standalone: false
})
export class ComposeDialogComponent {
  @Input() mailAccountId: string | null = null;

  /// Nombre y dirección de la cuenta, para el "De:" que se muestra.
  @Input() accountName: string | null = null;
  @Input() accountAddress: string | null = null;

  /// Se emite tras enviar, para que la pantalla refresque el listado y las marcas.
  @Output() sent = new EventEmitter<void>();

  public isVisible: boolean = false;
  public mode: MailboxComposeMode = 'new';

  public to: string[] = [];
  public cc: string[] = [];
  public bcc: string[] = [];
  public subject: string = '';
  public htmlBody: string = '';

  public showCc: boolean = false;
  public showBcc: boolean = false;

  public attachments: { fileId: string; fileName: string; size: number }[] = [];
  public forwardedParts: MailboxForwardedPart[] = [];

  public uploading: boolean = false;
  public sending: boolean = false;
  public error: MailboxError | null = null;

  private inReplyTo: MailboxDraft['inReplyTo'] = null;
  private references: string[] = [];

  /// Configuración del editor. Sin el complemento de variables ni el mapa de variables, que son cosa de las
  /// plantillas de expediente y aquí no aplican.
  public editorInit: any = {
    promotion: false,
    menubar: false,
    statusbar: false,
    height: 320,
    content_css: '/assets/tinymce/content.css',
    plugins: 'lists link table code autoresize',
    toolbar: 'undo redo | bold italic underline | forecolor backcolor | bullist numlist | link table | removeformat | code'
  };

  constructor(
    private mailboxService: MailboxService,
    private filesService: FilesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  get title(): string {
    switch (this.mode) {
      case 'reply': return 'Responder';
      case 'replyAll': return 'Responder a todos';
      case 'forward': return 'Reenviar';
      default: return 'Nuevo mensaje';
    }
  }

  get canSend(): boolean {
    return !this.sending && !this.uploading && (this.to.length > 0 || this.cc.length > 0 || this.bcc.length > 0);
  }

  /// Abre en blanco.
  openNew() {
    this.apply({ mode: 'new' });
  }

  /// Abre con el borrador que ha preparado el servidor.
  openWithDraft(draft: MailboxDraft) {
    this.apply({
      mode: draft.mode,
      to: draft.to.map(address => address.address),
      cc: draft.cc.map(address => address.address),
      subject: draft.subject,
      htmlBody: draft.htmlBody,
      forwardedParts: draft.forwardedParts ?? [],
      inReplyTo: draft.inReplyTo ?? null,
      references: draft.references ?? []
    });
  }

  /// Deja el compositor con exactamente estos datos y lo abre.
  ///
  /// Asigna todos los campos de una vez y **no pasa por vacío primero**. Antes llamaba a `reset()` y después ponía
  /// los del borrador: el editor recibía dos escrituras seguidas, y en la segunda respuesta se quedaba con la
  /// primera (la vacía). De ahí que respondiendo a un segundo mensaje el contenido saliera en blanco.
  private apply(values: {
    mode: MailboxComposeMode;
    to?: string[];
    cc?: string[];
    subject?: string;
    htmlBody?: string;
    forwardedParts?: MailboxForwardedPart[];
    inReplyTo?: MailboxDraft['inReplyTo'];
    references?: string[];
  }) {
    this.mode = values.mode;
    this.to = values.to ?? [];
    this.cc = values.cc ?? [];
    this.bcc = [];
    this.subject = values.subject ?? '';
    this.htmlBody = values.htmlBody ?? '';
    this.showCc = this.cc.length > 0;
    this.showBcc = false;
    this.attachments = [];
    this.forwardedParts = values.forwardedParts ?? [];
    this.inReplyTo = values.inReplyTo ?? null;
    this.references = values.references ?? [];
    this.error = null;
    this.sending = false;
    this.uploading = false;

    this.show();
  }

  private show() {
    this.isVisible = true;
  }

  /// Cierra, pidiendo confirmación si hay algo escrito: cerrar sin querer no debe tirar el mensaje.
  ///
  /// Se ignora si ya está cerrado: el diálogo emite su cambio de visibilidad también al terminar la animación de
  /// salida, y ese aviso rezagado podía llegar cuando ya se había vuelto a abrir para otro mensaje, borrando lo que
  /// se acababa de cargar.
  requestClose() {
    if (!this.isVisible) return;

    const hayContenido = this.to.length > 0 || this.cc.length > 0 || this.bcc.length > 0
      || !!this.subject.trim() || this.attachments.length > 0;

    if (!hayContenido) { this.close(); return; }

    this.confirmationService.confirm({
      header: 'Descartar el mensaje',
      message: '¿Descartar este mensaje? Se perderá lo que hayas escrito.',
      acceptLabel: 'Descartar',
      rejectLabel: 'Seguir escribiendo',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.close()
    });
  }

  private close() {
    this.isVisible = false;
    this.reset();
  }

  private reset() {
    this.mode = 'new';
    this.to = [];
    this.cc = [];
    this.bcc = [];
    this.subject = '';
    this.htmlBody = '';
    this.showCc = false;
    this.showBcc = false;
    this.attachments = [];
    this.forwardedParts = [];
    this.inReplyTo = null;
    this.references = [];
    this.error = null;
    this.sending = false;
    this.uploading = false;
  }

  /// Subida en dos pasos, el patrón de la aplicación: primero se crean los metadatos y después el binario.
  async onFileSelect(event: any) {
    this.uploading = true;
    this.error = null;

    for (const file of event.files ?? []) {
      try {
        const metadata = new GuaiFile();
        metadata.fileName = file.name;
        metadata.size = file.size;
        metadata.contentType = file.type;

        const created = await firstValueFrom(this.filesService.save(metadata));
        await firstValueFrom(this.filesService.upload(created.id as string, file));

        this.attachments.push({ fileId: created.id as string, fileName: file.name, size: file.size });
      } catch {
        this.messageService.add({
          severity: 'error', summary: 'Adjunto', closable: true,
          detail: `No se ha podido subir «${file.name}».`
        });
      }
    }

    this.uploading = false;
  }

  removeAttachment(fileId: string) {
    this.attachments = this.attachments.filter(attachment => attachment.fileId !== fileId);
  }

  removeForwardedPart(partId: string) {
    this.forwardedParts = this.forwardedParts.filter(part => part.partId !== partId);
  }

  sizeLabel(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  send() {
    if (!this.mailAccountId || !this.canSend) return;

    this.sending = true;
    this.error = null;

    const request: MailboxSendRequest = {
      mode: this.mode,
      to: this.to,
      cc: this.cc,
      bcc: this.bcc,
      subject: this.subject,
      htmlBody: this.htmlBody,
      attachments: this.attachments.map(attachment => ({ fileId: attachment.fileId })),
      forwardedParts: this.forwardedParts,
      inReplyTo: this.inReplyTo,
      references: this.references
    };

    this.mailboxService.send(this.mailAccountId, request).subscribe({
      next: result => {
        this.sending = false;

        //los avisos son cosas que no salieron perfectas pero que NO han impedido enviar: se cuentan como tal, sin
        //dar a entender que el correo no ha salido
        this.messageService.add({
          severity: result.warnings.length > 0 ? 'warn' : 'success',
          summary: 'Correo enviado',
          closable: true,
          detail: result.warnings.length > 0 ? result.warnings.join(' ') : 'El mensaje se ha enviado.',
          life: result.warnings.length > 0 ? 10000 : 4000
        });

        this.sent.emit();
        this.close();
      },
      error: (error: MailboxError) => {
        this.sending = false;
        this.error = error;
      }
    });
  }
}
